<script setup lang="ts">
import AppButton from "~/components/Common/Buttons/AppButton.vue";
import LoadButton from "~/components/Common/Buttons/LoadButton.vue";
import CommonInput from "~/components/Common/Inputs/CommonInput.vue";
import CenterModal from "~/components/Common/Modals/CenterModal.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import SimpleTable from "~/components/Common/Tables/SimpleTable.vue";
import { useAttendanceReview } from "~/composables/attendance/useAttendanceReview";
import { ATTENDANCE_MAX_HOURS } from "~/utils/helpers/attendance/review-rows";
import { useAttendanceReviewStore } from "~/store/attendance/attendance-review.store";

const store = useAttendanceReviewStore();
const { missingHeaders } = useAttendanceReview();

const isConfirming = ref(false);

/** Singular / plural on the only sentence that carries the count. */
const intro = computed(() => {
  const count = store.unsignedRows.length;
  const subject = count > 1 ? `${count} participants inscrits n'apparaissent` : "1 participant inscrit n'apparaît";

  return `${subject} pas sur la feuille. Ajustez leur temps effectué, ou laissez à 0 pour les marquer absents.`;
});

async function onConfirm() {
  await store.confirmAttendance();
  isConfirming.value = false;
}
</script>

<template>
  <!--
    Capped and laid out as a column: the card is centred by `translate(-50%, -50%)`, so a list long
    enough to outgrow the viewport put its own top at a negative offset — unreachable, because the
    backdrop's scroll never goes below 0. A whole session's roster can land in here.
  -->
  <CenterModal class="flex max-h-[85vh] w-full max-w-[488px] flex-col" @hide="store.closeMissingModal()">
    <template #body>
      <SvgIcon
        class="absolute right-4 top-4 text-secondary-color cursor-pointer w-[24px] h-[24px] z-10"
        name="close"
        @click="store.closeMissingModal()"
      />
      <div class="flex min-h-0 flex-1 flex-col p-[24px]">
        <h3 class="heading_4_demi text-primary-color pb-2 pr-8 shrink-0">Participants sans signature</h3>
        <p class="body_regular text-secondary-color shrink-0">{{ intro }}</p>

        <!--
          The list is the only part that scrolls; the question above it and the answer below stay put.

          The frame is carried here rather than by the table: `SimpleTable` draws its border and its
          rounding on the `<table>`, so both scrolled away with the content and left the list
          unframed as soon as it moved. Owned by the wrapper they stay put, and the wrapper's own
          `overflow` clips the content to the radius at every scroll position — hence the table's own
          frame being turned off below.
        -->
        <div class="mt-4 min-h-0 flex-1 overflow-y-auto rounded-lg border border-stroke-color">
          <!-- `!` because `SimpleTable` bakes `mt-4 border rounded-lg` in: same specificity otherwise. -->
          <SimpleTable
            class="!mt-0 !rounded-none !border-0"
            :headers="missingHeaders"
            :rows="store.unsignedRows"
            track-by="userId"
            :expand-first-column="false"
          >
            <template #row="{ row }">
              <td class="px-3 py-3 align-middle">
                <span class="body_regular text-primary-color">{{ row.displayValue }}</span>
              </td>
              <td class="px-3 py-3 align-middle">
                <div class="flex items-center justify-end gap-2">
                  <CommonInput
                    v-model="row.attendedHours"
                    type="number"
                    :min="0"
                    :max="Math.min(row.totalHours, ATTENDANCE_MAX_HOURS)"
                    placeholder="0"
                    class="max-w-[72px]"
                  />
                  <span class="body_regular whitespace-nowrap text-tertiary-color">/ {{ row.totalHours }} h</span>
                </div>
              </td>
            </template>
          </SimpleTable>
        </div>

        <div class="flex gap-4 justify-end mt-[24px] pt-[16px] border-t border-stroke-color shrink-0">
          <AppButton type="outlined" label="Retour au tableau" @click="store.closeMissingModal()" />
          <LoadButton v-model="isConfirming" text-confirm="Confirmer" force-waiting @click="onConfirm()" />
        </div>
      </div>
    </template>
  </CenterModal>
</template>
